import React, { useEffect, useRef, useState } from "react";
import { faChevronLeft, faChevronRight, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { Habit } from "../../types";
import {
    formatDateToLocal,
    getHabitBestStreak,
    getHabitCurrentStreak,
    getHabitProgressForDate,
    getHabitProgressRatioForDate,
    getHabitTargetCount,
    getHabitTotalCompletions,
    isHabitDateCompleted,
    isHabitPeriodCompleted,
    isPeriodHabit,
    isHabitDateTracked,
} from "../../utils/habitUtils";

import "./HabitHistoryModal.css";

interface HabitHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    element: Habit;
    onToggleComplete: (dateStr: string) => void;
    onSetProgress?: (dateStr: string, progress: number) => void;
    historyMonth?: Date;
    onHistoryMonthChange?: (date: Date) => void;
}

export const HabitHistoryModal: React.FC<HabitHistoryModalProps> = ({
    isOpen,
    onClose,
    element,
    onToggleComplete,
    onSetProgress,
    historyMonth: controlledHistoryMonth,
    onHistoryMonthChange,
}) => {
    const { t } = useTranslation();
    const [internalHistoryMonth, setInternalHistoryMonth] = useState(new Date());
    const [quickLogDate, setQuickLogDate] = useState<string | undefined>(undefined);
    const [quickLogValue, setQuickLogValue] = useState('');
    const quickLogRef = useRef<HTMLDivElement>(null);
    const historyMonth = controlledHistoryMonth || internalHistoryMonth;
    const setHistoryMonth = (date: Date) => {
        if (onHistoryMonthChange) {
            onHistoryMonthChange(date);
            return;
        }

        setInternalHistoryMonth(date);
    };

    const getMonthLabel = () => {
        const monthNames = [
            t('habitElement.january'),
            t('habitElement.february'),
            t('habitElement.march'),
            t('habitElement.april'),
            t('habitElement.may'),
            t('habitElement.june'),
            t('habitElement.july'),
            t('habitElement.august'),
            t('habitElement.september'),
            t('habitElement.october'),
            t('habitElement.november'),
            t('habitElement.december')
        ];
        return `${monthNames[historyMonth.getMonth()]} ${historyMonth.getFullYear()}`;
    };

    const isCurrentMonth = () => {
        const today = new Date();
        return historyMonth.getMonth() === today.getMonth() && historyMonth.getFullYear() === today.getFullYear();
    };

    const goToToday = () => {
        setHistoryMonth(new Date());
    };

    const getCalendarDataForMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        let startDay = firstDay.getDay();
        // Adjust to start from Monday (0 = Sunday, 1 = Monday, etc.)
        startDay = startDay === 0 ? 6 : startDay - 1;
        const totalDays = lastDay.getDate();
        
        const calendar = [];
        const today = new Date();
        const todayStr = formatDateToLocal(today);
        
        for (let i = 0; i < startDay; i++) {
            calendar.push({ day: '', dateStr: '', isCompleted: false, isToday: false, isEmpty: true, isFuture: false });
        }
        
        for (let day = 1; day <= totalDays; day++) {
            const currentDate = new Date(year, month, day);
            const dateStr = formatDateToLocal(currentDate);
            const isToday = dateStr === todayStr;
            const isFuture = currentDate > today;
            const isTracked = isHabitDateTracked(element, dateStr);
            const isCompleted = !isFuture && isHabitDateCompleted(element, dateStr);
            const progress = !isFuture ? getHabitProgressForDate(element, dateStr) : 0;
            const progressRatio = !isFuture ? getHabitProgressRatioForDate(element, dateStr) : 0;
            const isPeriodComplete = isPeriodHabit(element) && isHabitPeriodCompleted(element, dateStr);
            calendar.push({ day, dateStr, isCompleted, isPeriodComplete, isToday, isEmpty: false, isFuture, isTracked, progress, progressRatio });
        }
        
        return calendar;
    };

    const openQuickLog = (dateStr: string) => {
        setQuickLogDate(dateStr);
        setQuickLogValue(String(getHabitProgressForDate(element, dateStr) || ''));
    };

    const saveQuickLog = () => {
        if (!quickLogDate || !onSetProgress) return;
        onSetProgress(quickLogDate, Number(quickLogValue));
        setQuickLogDate(undefined);
        setQuickLogValue('');
    };

    useEffect(() => {
        if (!quickLogDate) return;

        const scrollTimer = window.setTimeout(() => {
            quickLogRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }, 80);

        return () => window.clearTimeout(scrollTimer);
    }, [quickLogDate]);

    if (!isOpen) return null;

    return (
        <div className="habit-history-modal-overlay" onClick={onClose}>
            <div className="habit-history-modal" onClick={(e) => e.stopPropagation()}>
                <div className="habit-history-header">
                    <div className="habit-history-header-content">
                        <h3>{t('habitElement.history')}</h3>
                        <span className="habit-history-habit-name">{element.title}</span>
                    </div>
                    <button className="habit-history-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                <div className="habit-history-navigation">
                    <button 
                        className="habit-history-nav-button"
                        onClick={() => {
                            const newMonth = new Date(historyMonth);
                            newMonth.setMonth(newMonth.getMonth() - 1);
                            setHistoryMonth(newMonth);
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span className="habit-history-month-label">{getMonthLabel()}</span>
                    <button 
                        className="habit-history-nav-button"
                        onClick={() => {
                            const newMonth = new Date(historyMonth);
                            newMonth.setMonth(newMonth.getMonth() + 1);
                            setHistoryMonth(newMonth);
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                    {!isCurrentMonth() && (
                        <button 
                            className="habit-history-today-button"
                            onClick={goToToday}
                        >
                            {t('habitElement.today')}
                        </button>
                    )}
                </div>
                <div className="habit-history-calendar">
                    <div className="habit-history-weekdays">
                        <div className="habit-history-weekday">{t('habitElement.monday')}</div>
                        <div className="habit-history-weekday">{t('habitElement.tuesday')}</div>
                        <div className="habit-history-weekday">{t('habitElement.wednesday')}</div>
                        <div className="habit-history-weekday">{t('habitElement.thursday')}</div>
                        <div className="habit-history-weekday">{t('habitElement.friday')}</div>
                        <div className="habit-history-weekday">{t('habitElement.saturday')}</div>
                        <div className="habit-history-weekday">{t('habitElement.sunday')}</div>
                    </div>
                    <div className="habit-history-days">
                        {getCalendarDataForMonth(historyMonth).map((day, index) => (
                            <div key={index} className="habit-history-day">
                                <div 
                                    className={`habit-history-day-circle ${day.isEmpty ? 'empty' : ''} ${day.isCompleted ? 'completed' : ''} ${day.isPeriodComplete && !day.isCompleted ? 'period-complete' : ''} ${(day.progress || 0) > 0 && !day.isCompleted ? 'partial' : ''} ${day.isToday ? 'today' : ''} ${day.isFuture ? 'future' : ''} ${!day.isEmpty && !day.isTracked ? 'untracked' : ''} ${quickLogDate === day.dateStr ? 'selected' : ''}`}
                                    style={{ '--habit-progress': `${Math.round((day.progressRatio || 0) * 360)}deg` } as React.CSSProperties}
                                    title={!day.isEmpty && (day.progress || 0) > 0 && getHabitTargetCount(element) > 1
                                        ? `${day.day}: ${day.progress}/${getHabitTargetCount(element)}`
                                        : String(day.day || '')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!day.isEmpty && !day.isFuture && day.isTracked) {
                                            if (getHabitTargetCount(element) > 1 && onSetProgress) {
                                                openQuickLog(day.dateStr);
                                            } else {
                                                onToggleComplete(day.dateStr);
                                            }
                                        }
                                    }}
                                >
                                    {!day.isEmpty && (
                                        <span className="habit-history-day-number">{day.day}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="habit-history-stats">
                    <div className="habit-history-stat">
                        <span className="habit-history-stat-value">{getHabitTotalCompletions(element)}</span>
                        <span className="habit-history-stat-label">{t('habitElement.total')}</span>
                    </div>
                    <div className="habit-history-stat">
                        <span className="habit-history-stat-value">{getHabitBestStreak(element)}</span>
                        <span className="habit-history-stat-label">{t('habitElement.bestStreak')}</span>
                    </div>
                    <div className="habit-history-stat">
                        <span className="habit-history-stat-value">{getHabitCurrentStreak(element)}</span>
                        <span className="habit-history-stat-label">{t('habitElement.currentStreak')}</span>
                    </div>
                </div>
                {quickLogDate && (
                    <div
                        ref={quickLogRef}
                        className="habit-history-quick-log"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4>{t('habitElement.logProgress')}</h4>
                        <div className="habit-history-quick-log-row">
                            <input
                                type="number"
                                min="0"
                                max={getHabitTargetCount(element)}
                                step="1"
                                value={quickLogValue}
                                onChange={(e) => setQuickLogValue(e.target.value)}
                                autoFocus
                            />
                            {element.unit && <span>{element.unit}</span>}
                        </div>
                        <div className="habit-history-quick-log-target">
                            {t('habitElement.target')}: {getHabitTargetCount(element)}{element.unit ? ` ${element.unit}` : ''}
                        </div>
                        <div className="habit-history-quick-log-actions">
                            <button type="button" onClick={() => setQuickLogDate(undefined)}>
                                {t('habitElement.cancel')}
                            </button>
                            <button type="button" className="primary" onClick={saveQuickLog}>
                                {t('habitElement.save')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
